import type { NoticePost } from '@/lib/types';
import { NoticeArticleBody, type ArticleView } from '@/pages/news/NoticeArticleBody';

/**
 * 공지 여럿의 <b>목록 줄</b> (사용자 지시 2026-11-02). 목록 창과, 창이 막혔을 때의 모달이 같은
 * 줄을 쓴다 — 차단을 푸는 순간 모양이 달라지지 않게. 이번에 펼쳐 본 줄은 흐리게 둔다.
 */
export function NoticePopupListItems({
  posts,
  read,
  onPick,
}: {
  posts: NoticePost[];
  read: string[];
  onPick: (post: NoticePost) => void;
}) {
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {posts.map((post) => (
        <li key={post.id} style={{ borderBottom: '1px solid var(--border)' }}>
          <button
            type="button"
            className="flex w-full items-center gap-3 text-left"
            style={{
              padding: '10px 4px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: read.includes(post.id) ? 'var(--text-muted)' : 'var(--text-primary)',
            }}
            onClick={() => onPick(post)}
          >
            <span className="min-w-0 flex-1 truncate" style={{ fontSize: '13px', fontWeight: 500 }}>
              {post.title}
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>
              {new Date(post.createdAt).toLocaleDateString('ko-KR')}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * 공지 팝업의 <b>속</b> — 제목 / 본문 / 날짜 / 아래 바.
 *
 * <h2>왜 떼어 놓았나</h2>
 * 쓰는 곳이 둘이다 — 실제로 뜨는 창({@code NoticePopupWindow})과 글쓰기 화면의
 * <b>팝업 미리보기</b>({@code NoticeEditorModal}). 각자 그리면 언젠가 어긋나고, 그러면 그건
 * 미리보기가 아니다(공지 본문이 {@code NoticeArticleBody} 하나를 나눠 쓰는 것과 같은 이유).
 *
 * <h2>액자에 넣어 볼 수도 있다</h2>
 * 진짜 창에서는 창 높이를 그대로 쓰고({@code 100dvh}), 미리보기에서는 <b>창만 한 상자</b>로
 * 그린다({@code framed}) — 모달 안에서 100dvh 를 쓰면 미리보기가 화면을 넘어간다.
 */
export function NoticePopupLayout({
  title,
  date,
  article,
  framed = false,
  linkImages = true,
  onDismissToday,
  onDismissForever,
  onClose,
  onBack,
}: {
  title: string;
  /** 이미 사람이 읽는 모양으로 만들어 넘긴다 — 이 컴포넌트는 날짜를 계산하지 않는다. */
  date: string;
  article: ArticleView;
  /** 미리보기처럼 <b>창이 아닌 자리</b>에 그릴 때. 창 높이 대신 고정 높이 상자가 된다. */
  framed?: boolean;
  /** 미리보기에서는 끈다 — 아직 저장되지 않은 사진이라 열어 볼 원본이 없다. */
  linkImages?: boolean;
  onDismissToday: () => void;
  onDismissForever: () => void;
  onClose: () => void;
  /** 목록 창에서 펼친 글이면 본문 위에 「← 목록」 이 선다. */
  onBack?: () => void;
}) {
  return (
    <div className={`notice-popup-window${framed ? ' notice-popup-window--framed' : ''}`}>
      {/*
        위 칸은 <b>제목뿐</b>이다(사용자 지시 2026-09-10). 두 줄에 밑줄까지 그으니 창 위쪽이
        머리말처럼 무거워졌는데, 팝업에서 위 칸은 <b>본문에 내주는 자리</b>다 — 이 창이 무엇인지는
        이미 창틀의 제목이 말한다(`document.title`).
      */}
      <div className="notice-popup-window-head">
        <h1 className="font-semibold" style={{ fontSize: '13px', color: 'var(--text-primary)', minWidth: 0 }}>
          {title}
        </h1>
      </div>

      {/* 분류 뱃지는 세우지 않는다 — 팝업에는 글이 한 건뿐이라 가를 것이 없다. */}
      <div className="notice-popup-window-body space-y-3">
        {onBack && (
          <button className="btn-ghost" style={{ fontSize: '12px' }} onClick={onBack}>
            ← 목록
          </button>
        )}
        <NoticeArticleBody article={article} showCategory={false} linkImages={linkImages} openLinksInNewTab />
      </div>

      {/*
        날짜는 <b>닫기 바로 위</b>다 — 오른쪽 끝에 고정 (사용자 지시 2026-09-10).

        본문 끝에 붙여 두었더니 글 길이에 따라 자리가 오르내렸다. 창을 열 때마다 날짜가 다른 데
        가 있으면 눈이 그것을 매번 찾는다. 스크롤하는 칸 <b>바깥</b>으로 빼면 어느 공지에서나
        같은 자리다 — 아래 오른쪽의 닫기 단추와 한 줄로 선다.
      */}
      <p className="notice-popup-window-date">{date}</p>

      {/* 아래 바 — 안 보기는 왼쪽, 닫기는 오른쪽(사용자 지시 2026-09-10). 본문 칸이 스크롤을
          맡으므로(`overflow-y`) 공지가 길어도 이 줄은 바닥에 남는다. */}
      <div className="notice-popup-window-actions">
        <button className="btn-ghost" style={{ fontSize: '12px' }} onClick={onDismissToday}>
          오늘 보지 않기
        </button>
        <button className="btn-ghost" style={{ fontSize: '12px' }} onClick={onDismissForever}>
          더이상 보지 않기
        </button>
        <button className="btn-primary ml-auto" style={{ fontSize: '13px', padding: '5px 16px' }} onClick={onClose}>
          닫기
        </button>
      </div>
    </div>
  );
}
